import { type FC } from 'react';
import { Popper, Paper, Typography, Button, Fade, Box, Collapse } from '@mui/material';
import PhotoOutlinedIcon from '@mui/icons-material/PhotoOutlined';

interface TourTooltipProps {
  anchorEl: HTMLElement | null;
  title: string;
  content: string;
  /** Optional image URL to display in the tooltip */
  image?: string;
  /** Whether the screenshot toggle is currently expanded */
  imageExpanded?: boolean;
  stepIndex: number;
  totalSteps: number;
  placement: 'top' | 'bottom' | 'left' | 'right';
  zIndex: number;
  visible: boolean;
  /** Element not found -- centered card with "not visible" badge and inline image */
  detached?: boolean;
  /** Centered positioning without detached semantics (image stays as toggle) */
  centered?: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  /** Called when the screenshot toggle is expanded/collapsed */
  onImageToggle?: (expanded: boolean) => void;
}

const TooltipCard: FC<{
  title: string;
  content: string;
  image?: string;
  imageExpanded: boolean;
  stepIndex: number;
  totalSteps: number;
  detached: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  onImageToggle?: (expanded: boolean) => void;
}> = ({ title, content, image, imageExpanded, stepIndex, totalSteps, detached, onNext, onBack, onSkip, onImageToggle }) => {
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;
  const showImageInline = detached && image;
  const showImageToggle = !detached && image;

  const hasVisibleImage = showImageInline || (showImageToggle && imageExpanded);
  const imageMaxWidth = showImageInline ? '80vw' : 560;

  return (
    <Paper
      elevation={8}
      sx={{
        p: 2.5,
        maxWidth: hasVisibleImage ? imageMaxWidth : 360,
        minWidth: 280,
        borderRadius: 2,
        transition: 'max-width 200ms ease-in-out',
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }} data-cy="tour-tooltip-title">
        {title}
      </Typography>
      {detached && (
        <Typography
          variant="caption"
          sx={{
            display: 'inline-block',
            mb: 1,
            px: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: 'action.hover',
            color: 'text.secondary',
          }}
        >
          Not visible on this page right now
        </Typography>
      )}
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: showImageInline ? 1.5 : 2, whiteSpace: 'pre-line' }} data-cy="tour-tooltip-content">
        {content}
      </Typography>

      {showImageToggle && (
        <Button
          size="small"
          startIcon={<PhotoOutlinedIcon fontSize="small" />}
          onClick={() => onImageToggle?.(!imageExpanded)}
          sx={{ textTransform: 'none', color: 'text.secondary', mb: imageExpanded ? 1 : 1.5, px: 0.5 }}
          data-cy="tour-btn-view-screenshot"
        >
          {imageExpanded ? 'Hide screenshot' : 'View screenshot'}
        </Button>
      )}

      {showImageToggle && (
        <Collapse in={imageExpanded}>
          <Box
            sx={{
              mb: 2,
              borderRadius: 1,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              maxHeight: '40vh',
            }}
          >
            <img
              src={image}
              alt={title}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
              data-cy="tour-tooltip-image"
            />
          </Box>
        </Collapse>
      )}

      {showImageInline && (
        <Box
          sx={{
            mb: 2,
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <img
            src={image}
            alt={title}
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
            }}
            data-cy="tour-tooltip-image"
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: 'text.disabled' }} data-cy="tour-step-counter">
          {stepIndex + 1} of {totalSteps}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            onClick={onSkip}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
            data-cy="tour-btn-skip"
          >
            Skip
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={isFirst}
            onClick={onBack}
            sx={{ textTransform: 'none' }}
            data-cy="tour-btn-back"
          >
            Back
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={onNext}
            sx={{ textTransform: 'none' }}
            data-cy="tour-btn-next"
          >
            {isLast ? 'Finish' : 'Next'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

const TourTooltip: FC<TourTooltipProps> = ({
  anchorEl,
  title,
  content,
  image,
  imageExpanded = false,
  stepIndex,
  totalSteps,
  placement,
  zIndex,
  visible,
  detached = false,
  centered = false,
  onNext,
  onBack,
  onSkip,
  onImageToggle,
}) => {
  if (!visible) return null;

  // Detached or centered mode: centered on screen, no anchor
  if (detached || centered) {
    return (
      <Fade in={visible} timeout={200}>
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex,
          }}
          data-cy="tour-tooltip"
        >
          <TooltipCard
            title={title}
            content={content}
            image={image}
            imageExpanded={imageExpanded}
            stepIndex={stepIndex}
            totalSteps={totalSteps}
            detached={detached}
            onNext={onNext}
            onBack={onBack}
            onSkip={onSkip}
            onImageToggle={onImageToggle}
          />
        </Box>
      </Fade>
    );
  }

  if (!anchorEl) return null;

  return (
    <Popper
      open={visible}
      anchorEl={anchorEl}
      placement={placement}
      transition
      modifiers={[
        { name: 'offset', options: { offset: [0, 16] } },
        { name: 'preventOverflow', options: { boundary: 'viewport', padding: 16 } },
        { name: 'flip', options: { fallbackPlacements: ['top', 'bottom', 'left', 'right'] } },
      ]}
      style={{ zIndex }}
      data-cy="tour-tooltip"
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={200}>
          <div>
            <TooltipCard
              title={title}
              content={content}
              image={image}
              imageExpanded={imageExpanded}
              stepIndex={stepIndex}
              totalSteps={totalSteps}
              detached={false}
              onNext={onNext}
              onBack={onBack}
              onSkip={onSkip}
              onImageToggle={onImageToggle}
            />
          </div>
        </Fade>
      )}
    </Popper>
  );
};

export default TourTooltip;
